import { Box, TableCell, TableHead, TableSortLabel } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { SortOrder } from "@bridgesplit/utils";

import { FONT_SIZES } from "../../theme";
import { Checkbox } from "../input";
import { TooltipText } from "../typography";
import { TableColumn } from "./types";
import { HEADER_CELL_SX, LEADING_COL_WIDTH, StyledTableRow, colFlexAlign, getCellPadding } from "./cell";
import { HeaderCheckboxState } from "./useSelection";

interface HeaderRowProps<TRow> {
    columns: TableColumn<TRow>[];
    isGrouped: boolean;
    orderBy: string;
    order: SortOrder;
    setOrder?: (o: SortOrder) => void;
    setOrderBy?: (key: string) => void;
    resetPage: () => void;
    headerCheckbox?: HeaderCheckboxState;
}

/**
 * The `<thead>` row. Renders an optional leading checkbox cell, a chevron spacer
 * cell in grouped mode, and the column titles with sort labels.
 */
export function HeaderRow<TRow>({
    columns,
    isGrouped,
    orderBy,
    order,
    setOrder,
    setOrderBy,
    resetPage,
    headerCheckbox
}: HeaderRowProps<TRow>) {
    const hasCheckbox = !!headerCheckbox;
    const leadingCells = (hasCheckbox ? 1 : 0) + (isGrouped ? 1 : 0);
    const totalColSpan = columns.length + leadingCells;

    return (
        <TableHead>
            <StyledTableRow>
                {hasCheckbox && (
                    <TableCell
                        sx={{
                            width: LEADING_COL_WIDTH,
                            ...HEADER_CELL_SX,
                            ...getCellPadding({ colIdx: 0, totalColSpan, isGrouped, hasCheckbox })
                        }}
                    >
                        <Checkbox
                            checked={headerCheckbox!.checked}
                            indeterminate={headerCheckbox!.indeterminate}
                            onChange={headerCheckbox!.onToggle}
                        />
                    </TableCell>
                )}
                {isGrouped && (
                    <TableCell
                        sx={{
                            width: LEADING_COL_WIDTH,
                            ...HEADER_CELL_SX,
                            ...getCellPadding({
                                colIdx: hasCheckbox ? 1 : 0,
                                totalColSpan,
                                isGrouped,
                                hasCheckbox
                            })
                        }}
                    />
                )}
                {columns.map((col, idx) => {
                    const colIdx = idx + leadingCells;
                    // Match the same key `onClick` sets below (`col.apiSortKey ?? col.dataIndex`).
                    // For an apiSortKey column, a stale `orderBy === dataIndex` reads as inactive so
                    // the next click corrects the key instead of just toggling direction.
                    const isActive = orderBy === (col.apiSortKey ?? col.dataIndex);
                    // Only surface the sort affordance when a click can actually sort: the column
                    // must have a `sortBy` or `apiSortKey` and the controller must expose `setOrderBy`.
                    // API-mode tables without a sort-wired controller (e.g. the portfolio sections)
                    // would otherwise show a header button that does nothing.
                    const sortAllowed = (!!col.sortBy || !!col.apiSortKey) && !!setOrderBy;
                    const direction = order === SortOrder.Desc ? "desc" : "asc";

                    return (
                        <TableCell
                            key={col.dataIndex}
                            align={col.align ?? "left"}
                            sortDirection={isActive ? direction : false}
                            sx={{
                                width: col.width ?? "auto",
                                ...HEADER_CELL_SX,
                                ...getCellPadding({ colIdx, totalColSpan, isGrouped, hasCheckbox })
                            }}
                        >
                            {sortAllowed ? (
                                <TableSortLabel
                                    active={isActive}
                                    direction={direction}
                                    onClick={() => {
                                        if (isActive) {
                                            setOrder?.(order === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc);
                                        } else {
                                            setOrderBy?.(col.apiSortKey ?? col.dataIndex);
                                        }
                                        resetPage();
                                    }}
                                >
                                    <HeaderTitle col={col} />
                                    {isActive && (
                                        <Box component="span" sx={{ ...visuallyHidden, fontSize: FONT_SIZES.body1 }}>
                                            {order === SortOrder.Desc ? "sorted descending" : "sorted ascending"}
                                        </Box>
                                    )}
                                </TableSortLabel>
                            ) : (
                                <HeaderTitle col={col} />
                            )}
                        </TableCell>
                    );
                })}
            </StyledTableRow>
        </TableHead>
    );
}

function HeaderTitle<TRow>({ col }: { col: TableColumn<TRow> }) {
    if (typeof col.title !== "string") {
        return <>{col.title}</>;
    }
    return (
        <TooltipText
            tooltipProps={{ placement: col.align === "center" ? "top" : col.align }}
            containerSx={{ width: "100%" }}
            helpText={col.tooltip}
            color="caption"
            variant="body2"
            sx={{
                width: "100%",
                whiteSpace: "nowrap",
                justifyContent: colFlexAlign(col)
            }}
        >
            {col.title}
        </TooltipText>
    );
}
